import React, { useState, useEffect } from 'react'
import InputMask from 'react-input-mask'
import { Auth } from 'aws-amplify'
import './InstallerDashboard.css'
import { useAuth } from '../../auth/Auth'
import { GpButton, GpFormField } from '../../components/gp'

const AppleGlyph = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
)

const AndroidGlyph = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <path
            d="M3.6 1.95c-.36.4-.6 1-.6 1.79V20.26c0 .8.24 1.4.6 1.79l9.36-9.36V11.31L3.6 1.95z"
            fill="#5BC9F4"
        />
        <path
            d="M16.81 14.69 12.96 12.69v-.69l3.85-2.01L20.4 12l-3.59 2.69z"
            fill="#FFCE00"
        />
        <path
            d="M12.96 12 3.6 22.05c.5.55 1.32.55 2.25.05l11.96-6.41-4.85-3.69z"
            fill="#FF453A"
        />
        <path
            d="M5.85 1.9C4.92 1.4 4.1 1.4 3.6 1.95L12.96 12l4.85-3.69L5.85 1.9z"
            fill="#2BB36C"
        />
    </svg>
)

const InstallerDashboard = () => {
    const auth = useAuth()
    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [phoneNum, setPhoneNum] = useState('')
    const [saving, setSaving] = useState(false)

    const email = auth.user?.attributes?.email || ''

    const reset = () => {
        setFirstName(auth.user?.attributes?.name || '')
        setLastName(auth.user?.attributes?.family_name || '')
        setPhoneNum(auth.user?.attributes?.phone_number?.substring(2) || '')
    }

    useEffect(() => {
        if (auth.user) reset()
    }, [auth.user])

    const save = () => {
        setSaving(true)
        Auth.currentAuthenticatedUser()
            .then((user) =>
                Auth.updateUserAttributes(user, {
                    name: firstName,
                    family_name: lastName,
                    phone_number: '+1' + (phoneNum || '').replace(/-/g, '').replace(/\s/g, ''),
                })
            )
            .finally(() => setSaving(false))
    }

    const downloadApp = (platform) => {
        const url =
            platform === 'ios'
                ? 'https://apps.apple.com/us/app/gopowerev/id1497166165'
                : 'https://play.google.com/store/apps/details?id=com.gopowerev.consumer'
        window.open(url, '_blank')
    }

    const initials = (firstName.charAt(0) + lastName.charAt(0)).toUpperCase() || 'NB'

    // Compare current form values to what's stored on the Cognito user so we
    // only enable Discard / Update when there's something to do.
    const savedFirst = auth.user?.attributes?.name || ''
    const savedLast = auth.user?.attributes?.family_name || ''
    const savedPhone = auth.user?.attributes?.phone_number?.substring(2) || ''
    const normalizePhone = (s) => (s || '').replace(/\s/g, '')
    const isDirty =
        firstName !== savedFirst ||
        lastName !== savedLast ||
        normalizePhone(phoneNum) !== normalizePhone(savedPhone)

    return (
        <div className="installer-home">
            <div style={{ marginBottom: 24 }}>
                <div
                    style={{
                        font: '500 11px/1 var(--gp-font)',
                        letterSpacing: 0.6,
                        textTransform: 'uppercase',
                        color: 'var(--gp-ink-mute)',
                    }}
                >
                    Installer
                </div>
                <h1 className="gp-page-title" style={{ marginTop: 4 }}>
                    Installer home
                </h1>
                <p className="gp-page-sub" style={{ marginBottom: 0 }}>
                    Keep your contact details current so property managers can reach you about
                    new installs.
                </p>
            </div>

            <div className="installer-grid">
                {/* Profile card */}
                <section
                    style={{
                        background: 'var(--gp-card)',
                        border: '1px solid var(--gp-hairline)',
                        borderRadius: 'var(--gp-r-lg)',
                        padding: 28,
                        boxShadow: 'var(--gp-shadow-sm)',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 14,
                            marginBottom: 22,
                        }}
                    >
                        <div
                            style={{
                                width: 48,
                                height: 48,
                                borderRadius: '50%',
                                background: 'var(--gp-lime-soft)',
                                color: 'var(--gp-brand)',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                font: '500 16px/1 var(--gp-font)',
                                letterSpacing: 0.3,
                                flexShrink: 0,
                            }}
                        >
                            {initials}
                        </div>
                        <div>
                            <div
                                style={{
                                    font: '500 17px/1.3 var(--gp-font)',
                                    color: 'var(--gp-ink)',
                                }}
                            >
                                Your profile
                            </div>
                            <div
                                style={{
                                    font: 'var(--gp-caption)',
                                    color: 'var(--gp-ink-mute)',
                                    marginTop: 2,
                                }}
                            >
                                Visible to property managers when you're assigned a job.
                            </div>
                        </div>
                    </div>

                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: 14,
                        }}
                    >
                        <GpFormField
                            label="First name"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                        />
                        <GpFormField
                            label="Last name"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                        />
                        <InputMask
                            mask="999-999-9999"
                            value={phoneNum}
                            onChange={(e) => setPhoneNum(e.target.value)}
                            maskChar=" "
                        >
                            {() => (
                                <GpFormField
                                    label="Phone"
                                    icon="phone"
                                    type="tel"
                                    placeholder="000-000-0000"
                                    inputStyle={{ font: '500 14px/1 var(--gp-mono)' }}
                                />
                            )}
                        </InputMask>
                        <GpFormField
                            label="Email"
                            type="email"
                            value={email}
                            readOnly
                            rowStyle={{ background: 'var(--gp-surface)' }}
                            inputStyle={{
                                color: 'var(--gp-ink-mute)',
                                cursor: 'not-allowed',
                            }}
                        />
                    </div>

                    <div
                        style={{
                            marginTop: 22,
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 10,
                        }}
                    >
                        <GpButton
                            variant="ghost"
                            onClick={reset}
                            disabled={saving || !isDirty}
                        >
                            Discard
                        </GpButton>
                        <GpButton
                            variant="primary"
                            onClick={save}
                            disabled={saving || !isDirty}
                        >
                            {saving ? 'Updating…' : 'Update'}
                        </GpButton>
                    </div>
                </section>

                {/* Green app-promo card */}
                <section
                    style={{
                        background: 'var(--gp-brand)',
                        color: '#FFFFFF',
                        borderRadius: 'var(--gp-r-lg)',
                        padding: 28,
                        position: 'relative',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 18,
                    }}
                >
                    <div
                        style={{
                            position: 'absolute',
                            right: -120,
                            top: -120,
                            width: 360,
                            height: 360,
                            borderRadius: '50%',
                            border: '1px solid rgba(182,242,115,0.16)',
                            pointerEvents: 'none',
                        }}
                    />
                    <div
                        style={{
                            position: 'absolute',
                            right: -40,
                            top: -40,
                            width: 220,
                            height: 220,
                            borderRadius: '50%',
                            border: '1px solid rgba(182,242,115,0.10)',
                            pointerEvents: 'none',
                        }}
                    />

                    <div style={{ position: 'relative' }}>
                        <span
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: '4px 10px',
                                borderRadius: 999,
                                background: 'rgba(182,242,115,0.18)',
                                color: 'var(--gp-lime)',
                                font: 'var(--gp-micro)',
                                letterSpacing: 0.3,
                            }}
                        >
                            Installer mobile app
                        </span>
                        <h2
                            style={{
                                font: '500 30px/1.1 var(--gp-font)',
                                margin: '14px 0 10px',
                                letterSpacing: -0.4,
                                color: '#FFFFFF',
                            }}
                        >
                            Get the GoPower Installer app
                        </h2>
                        <p
                            style={{
                                font: 'var(--gp-body)',
                                margin: 0,
                                color: 'rgba(255,255,255,0.85)',
                                lineHeight: 1.55,
                            }}
                        >
                            Commission new sites seamlessly. Pick up assigned jobs, scan
                            equipment and log installs straight from your phone.
                        </p>
                    </div>

                    <div
                        style={{
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 10,
                            marginTop: 'auto',
                        }}
                    >
                        <button
                            type="button"
                            onClick={() => downloadApp('ios')}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 10,
                                height: 48,
                                padding: '0 18px',
                                borderRadius: 999,
                                background: '#FFFFFF',
                                color: 'var(--gp-brand)',
                                font: '500 14px/1 var(--gp-font)',
                                border: 'none',
                                cursor: 'pointer',
                            }}
                        >
                            <AppleGlyph />
                            Download for iOS
                        </button>
                        <button
                            type="button"
                            onClick={() => downloadApp('android')}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 10,
                                height: 48,
                                padding: '0 18px',
                                borderRadius: 999,
                                background: 'transparent',
                                color: '#FFFFFF',
                                border: '1px solid rgba(255,255,255,0.7)',
                                font: '500 14px/1 var(--gp-font)',
                                cursor: 'pointer',
                            }}
                        >
                            <AndroidGlyph />
                            Download for Android
                        </button>
                    </div>
                </section>
            </div>
        </div>
    )
}

export default InstallerDashboard
